/**
 * Seasonal court times, always in Copenhagen time.
 *
 * The seasonal court endpoints do not agree on a zone: `court-details`, `court`,
 * `search` and `Slot/{id}` send UTC with an explicit `+00:00`, while
 * `GetSeasonalCourtPurchaseDetails` converts slot times to Copenhagen itself
 * and sends them without an offset. Printing the raw strings showed a 19:00
 * court as 18:00 (17:00 in summer) on some pages and 19:00 on others.
 *
 * So: a value **with** an offset is an instant and is converted; a value
 * **without** one is already Copenhagen wall-clock and is only reformatted.
 * Every formatted value carries its zone (CET or CEST), so a reader never has to
 * guess which of the two a page is showing.
 */

const ZONE = 'Europe/Copenhagen';

export const COPENHAGEN_TIME_NOTE = 'All times are Copenhagen time (CET in winter, CEST in summer).';

const hasOffset = value => /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);

const zoneFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: ZONE, timeZoneName: 'short' });

const zoneOf = instant => {
  const part = zoneFormatter.formatToParts(instant).find(p => p.type === 'timeZoneName');
  return part ? part.value : 'CET';
};

/**
 * `{ date: 'YYYY-MM-DD', time: 'HH:mm', zone: 'CET' | 'CEST' }`, or null for
 * an empty or unparsable value (and for .NET's `0001-01-01` "no date").
 */
export const copenhagenParts = value => {
  if (!value) return null;

  const text = String(value).trim().replace(' ', 'T');
  if (text.indexOf('0001-01-01') === 0) return null;

  if (hasOffset(text)) {
    const instant = new Date(text);
    if (Number.isNaN(instant.getTime())) return null;

    const [date, time] = instant.toLocaleString('sv-SE', { timeZone: ZONE }).split(' ');
    return { date, time: time.slice(0, 5), zone: zoneOf(instant) };
  }

  const [date, time = '00:00'] = text.split('T');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;

  // Already Copenhagen wall-clock. Reading it as UTC to pick the zone is off by
  // an hour or two, which only matters within hours of a DST switch, at night,
  // when no court is played.
  const approximate = new Date(`${date}T${time.slice(0, 5)}:00Z`);
  return {
    date,
    time: time.slice(0, 5),
    zone: Number.isNaN(approximate.getTime()) ? 'CET' : zoneOf(approximate)
  };
};

/** `2026-12-01 19:00 CET`, or `2026-12-01` with `dateOnly`; `fallback` when empty. */
export const formatCopenhagen = (value, { dateOnly = false, withZone = true, fallback = '-' } = {}) => {
  const parts = copenhagenParts(value);
  if (!parts) return fallback;
  if (dateOnly) return parts.date;
  return withZone ? `${parts.date} ${parts.time} ${parts.zone}` : `${parts.date} ${parts.time}`;
};

/** A court's recurring time (`19:00:00`), which is Copenhagen wall-clock by definition. */
export const formatCourtTime = value => (value ? `${String(value).slice(0, 5)}` : '');

/** Today's date in Copenhagen, `YYYY-MM-DD` — for date inputs that mean a Copenhagen day. */
export const copenhagenToday = () => new Date().toLocaleString('sv-SE', { timeZone: ZONE }).split(' ')[0];
